import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { ImpersonnalisationResult } from '@/features/auth/authentification/modeles/IImpersonnalisationProvider'
import { ensureSuccessStatusCode, useFetch } from '@/features/fetch'
import type { IResponse, IPagedResponse } from '@/features/api/extranet'
import type { UserDTO } from './modeles/UserDTO'

export const useImpersonalisationStore = defineStore('impersonalisationStore', () => {
  const interfaceVisible = ref(false)
  const impersonnalisation = ref<ImpersonnalisationResult>()

  function $reset() {
    interfaceVisible.value = false
    impersonnalisation.value = undefined
    _reject = null
    _resolve = null
  }

  const _fetch = useFetch('extranet')

  type Resolve<T> = (value: T | PromiseLike<T>) => void
  type Reject = (reason?: any) => void

  let _resolve: Resolve<ImpersonnalisationResult> | null = null
  let _reject: Reject | null

  function aficheDialogueImpersonnalisation(to: string) {
    if (_reject) {
      _reject('Impersonalisation a été appelée plusieurs fois')
    }
    $reset()

    // Gére l'impersonnalisation automatique
    const location = URL.parse(`http://localhost${to}`)
    if (location?.search) {
      const params = new URLSearchParams(location.search)
      const user = params.get('user-imperso')
      if (user) {
        rechercherUtilisateurImpersonnalisationAuto(user)
          .then(userInfo => {
            if (userInfo) {
              impersonnalisation.value = userInfo
              validerImpersonnalisationAsync()
            }
          })
      }
    }

    return new Promise<ImpersonnalisationResult>((resolve, reject) => {
      _resolve = resolve
      _reject = reject
      interfaceVisible.value = true
    })
  }

  async function validerImpersonnalisationAsync() {
    if (!impersonnalisation.value) return

    try {
      const url = `/impersonnalisation/${impersonnalisation.value.userId}`
      const response = await _fetch(url, { dialogMode: 'info' })
      ensureSuccessStatusCode(response)

      const tokenResponse = (await response.json()) as IResponse<string>
      const resultat = {
        ...impersonnalisation.value,
        token: tokenResponse.data,
      }
      interfaceVisible.value = false
      try {
        return _resolve!(resultat)
      } finally {
        $reset()
        // _reject = null
        // _resolve = null
      }
    } catch {
      // Ne rien faire
    }
  }

  async function abandonnerImpersonnalisationAsync() {
    interfaceVisible.value = false
    try {
      await _reject!('Abandon utilisateur')
    } finally {
      $reset()
      // _reject = null
      // _resolve = null
    }
  }

  async function searchImpersonnalisation(value: string) {
    const query = value.trim().toLowerCase()
    const response = await _fetch(`/impersonnalisation?search=${query}`)
    if (response.ok) {
      const result = (await response.json()) as IPagedResponse<UserDTO>
      return result.data.map((dto) => {
        return {
          ...dto,
          token: '',
        } as ImpersonnalisationResult
      })
    } else return []
  }

  async function rechercherUtilisateurImpersonnalisationAuto(value: string) {
    // Vérifie l'existance de l'utilisateur à impersonnaliser
    const resultatRecheche = await searchImpersonnalisation(value)
    if (resultatRecheche?.length !== 1) return
    return resultatRecheche[0]
  }

  return {
    interfaceVisible,
    impersonnalisation,
    searchImpersonnalisation,
    aficheDialogueImpersonnalisation,
    validerImpersonnalisationAsync,
    abandonnerImpersonnalisationAsync,
    $reset,
  }
})
